import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import axios from "axios";
import { toast } from "sonner";
import { 
  MessageCircle, 
  QrCode, 
  Send, 
  RefreshCw, 
  CheckCircle2,
  Loader2,
  LogOut,
  Wifi,
  WifiOff,
  Smartphone,
  Image as ImageIcon,
  Check,
  CheckCheck,
  AlertTriangle,
  Settings
} from "lucide-react";
import QRCode from 'qrcode';
import { useNavigate } from "react-router-dom";

export default function WhatsApp() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("connect"); // connect, chats
  const [connectionStatus, setConnectionStatus] = useState({ connected: false, status: "disconnected" });
  const [serviceStatus, setServiceStatus] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const messagesEndRef = useRef(null);
  const lastMessageCountRef = useRef(0);
  const isInitialLoadRef = useRef(true);

  // Check service status e connection status periodicamente
  useEffect(() => {
    checkServiceStatus();
    checkStatus();
    const interval = setInterval(() => {
      checkServiceStatus();
      checkStatus();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const checkServiceStatus = async () => {
    try {
      const response = await axios.get(`${API}/system/whatsapp-service/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Só atualiza se os dados mudaram para evitar flickering
      setServiceStatus(prev => {
        if (JSON.stringify(prev) === JSON.stringify(response.data)) return prev;
        return response.data;
      });
    } catch (error) {
      setServiceStatus(prev => {
        const newStatus = { service_running: false, node_installed: false };
        if (JSON.stringify(prev) === JSON.stringify(newStatus)) return prev;
        return newStatus;
      });
    }
  };

  // Quando conecta, carrega chats
  useEffect(() => {
    if (connectionStatus.connected) {
      loadChats();
      setActiveTab("chats");
    }
  }, [connectionStatus.connected]);

  // Polling de mensagens quando chat selecionado
  useEffect(() => {
    if (!selectedChat || !connectionStatus.connected) return;
    
    const pollMessages = async () => {
      await loadMessagesIncremental(selectedChat.id);
    };
    
    // Poll a cada 3 segundos
    const interval = setInterval(pollMessages, 3000);
    return () => clearInterval(interval);
  }, [selectedChat, connectionStatus.connected]);

  // Scroll para o fim APENAS quando há novas mensagens
  useEffect(() => {
    if (messages.length > lastMessageCountRef.current) {
      // Só faz scroll se realmente tem mensagens novas
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
    lastMessageCountRef.current = messages.length;
  }, [messages.length]);

  // Generate QR code image from string
  useEffect(() => {
    if (qrCode) {
      QRCode.toDataURL(qrCode, { width: 280, margin: 2 })
        .then(url => setQrImageUrl(url))
        .catch(err => console.error('QR generation error:', err));
    } else {
      setQrImageUrl(null);
    }
  }, [qrCode]);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Só atualiza se os dados mudaram para evitar flickering
      setConnectionStatus(prev => {
        if (prev.connected === response.data.connected && prev.status === response.data.status) {
          return prev;
        }
        return response.data;
      });
      
      if (response.data.connected) {
        setQrCode(null);
        setQrImageUrl(null);
      }
    } catch (error) {
      setConnectionStatus(prev => {
        if (!prev.connected && prev.status === "error") return prev;
        return { connected: false, status: "error" };
      });
    }
  };

  const connectWhatsApp = async () => {
    setQrLoading(true);
    try {
      // Trigger connection and get QR in one call
      const response = await axios.post(`${API}/whatsapp/connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Se o connect já retornou o QR code, usar direto
      if (response.data.qrcode) {
        setQrCode(response.data.qrcode);
      } else {
        // Fallback: tentar buscar QR code
        await new Promise(resolve => setTimeout(resolve, 2000));
        await loadQrCode();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao conectar");
    } finally {
      setQrLoading(false);
    }
  };

  const forceNewQR = async () => {
    setQrLoading(true);
    try {
      const response = await axios.post(`${API}/whatsapp/force-new-qr`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.qrcode) {
        setQrCode(response.data.qrcode);
        toast.success("Novo QR Code gerado!");
      } else {
        // Fallback: tentar buscar QR code algumas vezes
        let attempts = 0;
        const maxAttempts = 5;
        while (attempts < maxAttempts) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          const qrResponse = await axios.get(`${API}/whatsapp/qrcode`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (qrResponse.data.qrcode) {
            setQrCode(qrResponse.data.qrcode);
            toast.success("Novo QR Code gerado!");
            break;
          }
          attempts++;
        }
        
        if (attempts >= maxAttempts) {
          toast.error("QR Code não gerado. Tente novamente em alguns segundos.");
        }
      }
    } catch (error) {
      toast.error("Erro ao gerar novo QR Code");
    } finally {
      setQrLoading(false);
    }
  };

  const loadQrCode = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/qrcode`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.qrcode) {
        setQrCode(response.data.qrcode);
      }
      
      if (response.data.status === "connected") {
        setConnectionStatus({ connected: true, status: "connected" });
        setQrCode(null);
      }
    } catch (error) {
      console.error("Error loading QR:", error);
    }
  };

  const loadChats = async () => {
    try {
      const response = await axios.get(`${API}/whatsapp/chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(response.data)) {
        setChats(response.data);
      }
    } catch (error) {
      console.error("Error loading chats:", error);
    }
  };

  const loadMessages = async (chatId) => {
    try {
      const response = await axios.get(`${API}/whatsapp/messages/${encodeURIComponent(chatId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(response.data)) {
        isInitialLoadRef.current = true;
        lastMessageCountRef.current = 0;
        setMessages(response.data);
      }
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  // Carrega mensagens de forma incremental (sem resetar scroll)
  const loadMessagesIncremental = async (chatId) => {
    try {
      const response = await axios.get(`${API}/whatsapp/messages/${encodeURIComponent(chatId)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (Array.isArray(response.data)) {
        // Só atualiza se o número de mensagens mudou
        if (response.data.length !== messages.length) {
          isInitialLoadRef.current = false;
          setMessages(response.data);
        }
      }
    } catch (error) {
      console.error("Error loading messages:", error);
    }
  };

  const selectChat = (chat) => {
    setSelectedChat(chat);
    loadMessages(chat.id);
  };

  const sendMessage = async () => {
    if (!newMessage.trim()) return;
    
    const number = selectedChat ? selectedChat.id : newNumber;
    if (!number) {
      toast.error("Selecione um chat ou informe um número");
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(`${API}/whatsapp/send`, {
        number: number.replace("@s.whatsapp.net", ""),
        message: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewMessage("");
      toast.success("Mensagem enviada!");
      
      if (selectedChat) {
        setTimeout(() => loadMessages(selectedChat.id), 1000);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar mensagem");
    } finally {
      setSendingMessage(false);
    }
  };

  const disconnect = async () => {
    try {
      await axios.post(`${API}/whatsapp/disconnect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnectionStatus({ connected: false, status: "disconnected" });
      setQrCode(null);
      setQrImageUrl(null);
      setChats([]);
      setSelectedChat(null);
      setMessages([]);
      setActiveTab("connect");
      toast.success("Desconectado com sucesso");
    } catch (error) {
      toast.error("Erro ao desconectar");
    }
  };

  const formatPhoneNumber = (jid) => {
    if (!jid) return "";
    return jid.replace("@s.whatsapp.net", "").replace("@g.us", " (Grupo)");
  };

  const formatMessageTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Sidebar>
      <div className="p-6 lg:p-8" data-testid="whatsapp-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-chivo font-bold text-3xl tracking-tight mb-1">WhatsApp</h1>
            <p className="text-zinc-500 text-sm">Conecte seu WhatsApp e gerencie conversas</p>
          </div>
          
          {/* Connection Status */}
          <div className={`flex items-center gap-2 px-4 py-2 border ${
            connectionStatus.connected 
              ? 'border-green-200 bg-green-50 text-green-700' 
              : 'border-zinc-200 bg-zinc-50 text-zinc-600'
          }`}>
            {connectionStatus.connected ? (
              <>
                <Wifi size={18} />
                <span className="text-sm font-medium">Conectado</span>
              </>
            ) : (
              <>
                <WifiOff size={18} />
                <span className="text-sm font-medium">Desconectado</span>
              </>
            )}
          </div>
        </div>

        {/* Service Not Running Alert */}
        {serviceStatus && !serviceStatus.service_running && (
          <div className="bg-amber-50 border border-amber-200 p-6 mb-6">
            <div className="flex gap-4">
              <AlertTriangle className="w-8 h-8 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-amber-800 mb-2">Serviço WhatsApp não está rodando</h3>
                <p className="text-sm text-amber-700 mb-4">
                  {!serviceStatus.node_installed 
                    ? "O Node.js precisa ser instalado primeiro. Vá para as Configurações e clique em 'Instalar Node.js'."
                    : "O serviço WhatsApp precisa ser iniciado. Vá para as Configurações e clique em 'Iniciar Serviço'."}
                </p>
                <Button
                  onClick={() => navigate('/settings')}
                  className="rounded-none bg-amber-600 hover:bg-amber-700 text-white gap-2"
                >
                  <Settings size={16} />
                  Ir para Configurações
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === "connect" ? "default" : "outline"}
            onClick={() => setActiveTab("connect")}
            className="rounded-none gap-2"
          >
            <QrCode size={16} />
            Conexão
          </Button>
          <Button
            variant={activeTab === "chats" ? "default" : "outline"}
            onClick={() => { setActiveTab("chats"); if (connectionStatus.connected) loadChats(); }}
            disabled={!connectionStatus.connected}
            className="rounded-none gap-2"
          >
            <MessageCircle size={16} />
            Conversas
          </Button>
        </div>

        {/* Connect Tab */}
        {activeTab === "connect" && (
          <div className="bg-white border border-zinc-200 p-6 max-w-xl">
            <h2 className="font-chivo font-bold text-lg mb-4">Conectar WhatsApp</h2>
            
            {connectionStatus.connected ? (
              <div className="text-center py-8">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <p className="text-lg font-medium text-green-700 mb-2">WhatsApp Conectado!</p>
                <p className="text-sm text-zinc-500 mb-6">Você já pode enviar e receber mensagens.</p>
                
                <div className="flex gap-3 justify-center">
                  <Button
                    onClick={() => setActiveTab("chats")}
                    className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white gap-2"
                  >
                    <MessageCircle size={16} />
                    Ver Conversas
                  </Button>
                  <Button
                    onClick={disconnect}
                    variant="outline"
                    className="rounded-none border-red-200 text-red-600 hover:bg-red-50 gap-2"
                  >
                    <LogOut size={16} />
                    Desconectar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center">
                {qrImageUrl ? (
                  <div className="mb-4">
                    <img 
                      src={qrImageUrl}
                      alt="QR Code WhatsApp"
                      className="mx-auto border border-zinc-200 p-4 bg-white"
                    />
                    <div className="mt-4 p-4 bg-zinc-50 border border-zinc-200">
                      <div className="flex items-center justify-center gap-3 mb-2">
                        <Smartphone className="text-zinc-600" size={20} />
                        <span className="font-medium text-sm">Como conectar:</span>
                      </div>
                      <ol className="text-sm text-zinc-600 text-left space-y-1">
                        <li>1. Abra o WhatsApp no celular</li>
                        <li>2. Toque em ⋮ → Dispositivos conectados</li>
                        <li>3. Toque em "Conectar dispositivo"</li>
                        <li>4. Escaneie este QR Code</li>
                      </ol>
                    </div>
                    
                    <div className="flex gap-2 mt-4 justify-center">
                      <Button
                        onClick={loadQrCode}
                        variant="outline"
                        className="rounded-none gap-2"
                      >
                        <RefreshCw size={16} />
                        Atualizar QR
                      </Button>
                      <Button
                        onClick={forceNewQR}
                        variant="outline"
                        disabled={qrLoading}
                        className="rounded-none gap-2 border-orange-200 text-orange-600 hover:bg-orange-50"
                      >
                        <QrCode size={16} />
                        Gerar Novo QR
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="py-12">
                    <QrCode className="w-20 h-20 text-zinc-300 mx-auto mb-4" />
                    <p className="text-zinc-500 mb-6">Clique para gerar o QR Code e conectar seu WhatsApp</p>
                    
                    <div className="flex flex-col gap-3 items-center">
                      <Button
                        onClick={connectWhatsApp}
                        disabled={qrLoading}
                        className="rounded-none bg-green-600 hover:bg-green-700 text-white gap-2 px-8 h-12"
                        data-testid="connect-whatsapp-btn"
                      >
                        {qrLoading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            Gerando QR Code...
                          </>
                        ) : (
                          <>
                            <QrCode size={20} />
                            Conectar WhatsApp
                          </>
                        )}
                      </Button>
                      
                      <Button
                        onClick={forceNewQR}
                        variant="outline"
                        disabled={qrLoading}
                        className="rounded-none gap-2 text-sm"
                      >
                        <RefreshCw size={14} />
                        Forçar Novo QR Code
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Chats Tab */}
        {activeTab === "chats" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Chat List */}
            <div className="bg-white border border-zinc-200">
              <div className="p-4 border-b border-zinc-200">
                <div className="flex items-center justify-between">
                  <h2 className="font-chivo font-bold">Conversas</h2>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={loadChats}
                    className="h-8 w-8"
                  >
                    <RefreshCw size={14} />
                  </Button>
                </div>
              </div>
              
              <div className="max-h-[500px] overflow-y-auto">
                {chats.length === 0 ? (
                  <div className="p-8 text-center text-zinc-400">
                    <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Nenhuma conversa ainda</p>
                    <p className="text-xs mt-1">Envie uma mensagem para iniciar</p>
                  </div>
                ) : (
                  chats.map((chat) => (
                    <button
                      key={chat.id}
                      onClick={() => selectChat(chat)}
                      className={`w-full p-4 text-left border-b border-zinc-100 hover:bg-zinc-50 transition-colors ${
                        selectedChat?.id === chat.id ? 'bg-zinc-100' : ''
                      }`}
                    >
                      <p className="font-medium text-sm truncate">
                        {chat.name || formatPhoneNumber(chat.id)}
                      </p>
                      {chat.messageCount > 0 && (
                        <p className="text-xs text-zinc-500 mt-1">
                          {chat.messageCount} mensagem(ns)
                        </p>
                      )}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="lg:col-span-2 bg-white border border-zinc-200 flex flex-col">
              {selectedChat ? (
                <>
                  {/* Chat Header */}
                  <div className="p-4 border-b border-zinc-200 bg-zinc-50">
                    <p className="font-chivo font-bold">
                      {selectedChat.name || formatPhoneNumber(selectedChat.id)}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {formatPhoneNumber(selectedChat.id)}
                    </p>
                  </div>
                  
                  {/* Messages */}
                  <div className="flex-1 p-4 overflow-y-auto max-h-[400px] bg-zinc-50">
                    {messages.length === 0 ? (
                      <div className="text-center text-zinc-400 py-8">
                        <p className="text-sm">Nenhuma mensagem</p>
                      </div>
                    ) : (
                      messages.map((msg, idx) => {
                        const isFromMe = msg.key?.fromMe;
                        const hasImage = msg.message?.imageMessage;
                        const textContent = msg.message?.conversation || 
                                           msg.message?.extendedTextMessage?.text ||
                                           msg.message?.imageMessage?.caption || "";
                        
                        return (
                          <div
                            key={idx}
                            className={`mb-3 flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                          >
                            <div className={`max-w-[70%] ${
                              isFromMe 
                                ? 'bg-green-100 border border-green-200' 
                                : 'bg-white border border-zinc-200'
                            }`}>
                              {/* Sender indicator */}
                              {!isFromMe && (
                                <div className="px-3 pt-2 pb-1 border-b border-zinc-100">
                                  <span className="text-xs font-medium text-violet-600">
                                    {msg.pushName || formatPhoneNumber(msg.key?.remoteJid)}
                                  </span>
                                </div>
                              )}
                              
                              {/* Image Preview */}
                              {hasImage && (
                                <div className="p-2">
                                  <div className="relative bg-zinc-100 rounded overflow-hidden">
                                    {msg.imageUrl ? (
                                      <img 
                                        src={msg.imageUrl} 
                                        alt="Imagem" 
                                        className="max-w-full max-h-48 object-contain"
                                        onClick={() => window.open(msg.imageUrl, '_blank')}
                                        style={{ cursor: 'pointer' }}
                                      />
                                    ) : (
                                      <div className="flex items-center justify-center p-8 bg-zinc-100">
                                        <div className="text-center">
                                          <ImageIcon className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
                                          <span className="text-xs text-zinc-500">
                                            {msg.message?.imageMessage?.mimetype || "Imagem"}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Text content */}
                              {textContent && (
                                <div className="px-3 py-2">
                                  <p className="text-sm whitespace-pre-wrap">
                                    {textContent}
                                  </p>
                                </div>
                              )}
                              
                              {/* No content fallback */}
                              {!hasImage && !textContent && (
                                <div className="px-3 py-2">
                                  <p className="text-sm text-zinc-400 italic">
                                    [Mídia não suportada]
                                  </p>
                                </div>
                              )}
                              
                              {/* Footer with time and status */}
                              <div className="px-3 pb-2 flex items-center justify-end gap-1">
                                <span className="text-xs text-zinc-400">
                                  {formatMessageTime(msg.messageTimestamp)}
                                </span>
                                {isFromMe && (
                                  <span className="text-green-600">
                                    {msg.status === 'read' ? (
                                      <CheckCheck className="w-3.5 h-3.5" />
                                    ) : msg.status === 'delivered' ? (
                                      <CheckCheck className="w-3.5 h-3.5 text-zinc-400" />
                                    ) : (
                                      <Check className="w-3.5 h-3.5 text-zinc-400" />
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </div>
                  
                  {/* Send Message */}
                  <div className="p-4 border-t border-zinc-200">
                    <div className="flex gap-2">
                      <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Digite sua mensagem..."
                        className="flex-1 h-10 rounded-none"
                        onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                        data-testid="message-input"
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="h-10 px-4 rounded-none bg-green-600 hover:bg-green-700 text-white"
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send size={16} />}
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center p-8">
                    <MessageCircle className="w-12 h-12 mx-auto mb-3 text-zinc-300" />
                    <p className="text-zinc-500 mb-4">Selecione uma conversa ou inicie uma nova</p>
                    
                    {/* New conversation */}
                    <div className="max-w-sm mx-auto p-4 border border-zinc-200 bg-zinc-50">
                      <p className="text-sm font-medium text-zinc-700 mb-3">Nova mensagem</p>
                      <Input
                        value={newNumber}
                        onChange={(e) => setNewNumber(e.target.value)}
                        placeholder="Número: 5511999999999"
                        className="h-10 rounded-none text-sm mb-2"
                        data-testid="new-number-input"
                      />
                      <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Mensagem..."
                        className="rounded-none text-sm mb-2"
                        rows={2}
                      />
                      <Button
                        onClick={sendMessage}
                        disabled={sendingMessage || !newNumber || !newMessage.trim()}
                        className="w-full h-10 rounded-none bg-green-600 hover:bg-green-700 text-white"
                      >
                        {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar Mensagem"}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Sidebar>
  );
}
